import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authHeaders } from '../services/auth';
import { kvGet, kvPut } from '../services/cloudStorage';
import LanguageSelector from './LanguageSelector';
import HelpPanel from './HelpPanel';
import ApiKeyPanel from './ApiKeyPanel';
import './UserSettings.css';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || '';

export default function UserSettings({ onClose, onAdmin, isAdmin, user, onUpdateUser }) {
  const { t } = useTranslation();

  // Username change
  const [newUname,    setNewUname]    = useState('');
  const [unameStatus, setUnameStatus] = useState('');

  // Password change
  const [curPw,    setCurPw]    = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [pwStatus, setPwStatus] = useState('');

  // Profile (email, phone)
  const [profEmail,  setProfEmail]  = useState('');
  const [profPhone,  setProfPhone]  = useState('');
  const [profStatus, setProfStatus] = useState('');

  // Nome Gruppo (admin only)
  const [groupNameVal,    setGroupNameVal]    = useState('');
  const [groupNameStatus, setGroupNameStatus] = useState('');

  const [showHelp,   setShowHelp]   = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);


  useEffect(() => {
    if (!WORKER_URL) return;
    fetch(`${WORKER_URL}/api/user/profile`, { headers: authHeaders(), credentials: 'include' })
      .then(r => r.json())
      .then(d => { setProfEmail(d.email||''); setProfPhone(d.phone||''); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    kvGet('group:group_settings').then(s => { if (s?.groupName) setGroupNameVal(s.groupName); });
  }, [isAdmin]);


  const changeUsername = async () => {
    const val = newUname.trim();
    if (!val) return;
    try {
      const r = await fetch(`${WORKER_URL}/api/user/username`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ username: val }),
      });
      const d = await r.json();
      if (!r.ok) { setUnameStatus(`⚠ ${d.error}`); return; }
      setUnameStatus('✓ Username aggiornato');
      setNewUname('');
      if (onUpdateUser) onUpdateUser(d.user, d.token);
    } catch { setUnameStatus('⚠ Errore connessione'); }
  };

  const saveGroupName = async () => {
    const current = await kvGet('group:group_settings') || {};
    await kvPut('group:group_settings', { ...current, groupName: groupNameVal.trim() });
    setGroupNameStatus('✓ Nome salvato');
    setTimeout(() => setGroupNameStatus(''), 2000);
  };

  const saveProfile = async () => {
    try {
      const r = await fetch(`${WORKER_URL}/api/user/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ email: profEmail.trim(), phone: profPhone.trim() }),
      });
      if (r.ok) { setProfStatus('✓ Profilo aggiornato'); }
      else { const e = await r.json(); setProfStatus(`⚠ ${e.error}`); }
    } catch { setProfStatus('⚠ Errore connessione'); }
  };

  const changePassword = async () => {
    if (!curPw || !newPw) return;
    if (newPw.length < 12) { setPwStatus('⚠ Minimo 12 caratteri'); return; }
    try {
      const r = await fetch(`${WORKER_URL}/api/user/password`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }),
      });
      const d = await r.json();
      if (!r.ok) { setPwStatus(`⚠ ${d.error}`); return; }
      setPwStatus('✓ Password aggiornata');
      setCurPw(''); setNewPw('');
    } catch { setPwStatus('⚠ Errore connessione'); }
  };

  return (
    <div className="uset-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="uset-box">
        <div className="uset-header">
          <span>{t('settings.title')}</span>
          <button className="uset-close" onClick={onClose}>[X]</button>
        </div>

        {isAdmin && onAdmin && (
          <button className="uset-admin-btn" onClick={onAdmin}>⚙ GESTIONE UTENTI</button>
        )}
        {isAdmin && (
          <button className="uset-admin-btn" onClick={() => setShowApiKey(true)}>🤖 API KEY GEMINI</button>
        )}

        <button className="uset-help-btn" onClick={() => setShowHelp(true)}>❓ HELP & ISTRUZIONI</button>

        {showHelp   && <HelpPanel   onClose={() => setShowHelp(false)}   />}
        {showApiKey && <ApiKeyPanel onClose={() => setShowApiKey(false)} />}

        {isAdmin && (
          <div className="uset-section">
            <div className="uset-sec-lbl">// NOME GRUPPO</div>
            <input className="uset-input" placeholder="nome del gruppo..."
              value={groupNameVal}
              onChange={e => { setGroupNameVal(e.target.value); setGroupNameStatus(''); }}
              onKeyDown={e => e.key === 'Enter' && saveGroupName()} />
            <button className="uset-save" onClick={saveGroupName} disabled={!groupNameVal.trim()}>
              AGGIORNA
            </button>
            {groupNameStatus && <div className="uset-status">{groupNameStatus}</div>}
          </div>
        )}

        <div className="uset-section">
          <div className="uset-sec-lbl">// LINGUA</div>
          <LanguageSelector />
        </div>

        <div className="uset-section">
          <div className="uset-sec-lbl">// USERNAME</div>
          <div className="uset-cur">attuale: <span>{user?.username}</span></div>
          <input className="uset-input" placeholder="nuovo username..."
            value={newUname}
            onChange={e => { setNewUname(e.target.value); setUnameStatus(''); }}
            onKeyDown={e => e.key === 'Enter' && changeUsername()} />
          <button className="uset-save" onClick={changeUsername} disabled={!newUname.trim()}>
            AGGIORNA
          </button>
          {unameStatus && <div className="uset-status">{unameStatus}</div>}
        </div>

        <div className="uset-section">
          <div className="uset-sec-lbl">// PROFILO</div>
          <input className="uset-input" placeholder="email..." type="email"
            value={profEmail}
            onChange={e => { setProfEmail(e.target.value); setProfStatus(''); }} />
          <input className="uset-input" placeholder="telefono..." type="tel"
            value={profPhone}
            onChange={e => { setProfPhone(e.target.value); setProfStatus(''); }}
            onKeyDown={e => e.key === 'Enter' && saveProfile()} />
          <button className="uset-save" onClick={saveProfile}>
            AGGIORNA
          </button>
          {profStatus && <div className="uset-status">{profStatus}</div>}
        </div>

        <div className="uset-section">
          <div className="uset-sec-lbl">// PASSWORD</div>
          <input className="uset-input" type="password" placeholder="password attuale..."
            value={curPw}
            onChange={e => { setCurPw(e.target.value); setPwStatus(''); }} />
          <input className="uset-input" type="password"
            placeholder="nuova password (min 12, maiusc, num, simbolo)..."
            value={newPw}
            onChange={e => { setNewPw(e.target.value); setPwStatus(''); }}
            onKeyDown={e => e.key === 'Enter' && changePassword()} />
          <button className="uset-save" onClick={changePassword} disabled={!curPw || !newPw}>
            AGGIORNA
          </button>
          {pwStatus && <div className="uset-status">{pwStatus}</div>}
        </div>

      </div>
    </div>
  );
}
